// ./src/components/generic/VariableMenu/VariableMenu.tsx
//
// The ONE "Insert variable" menu, used by every editor (RichText / Markdown / HTML) and by plain inputs that take a
// token (a message subject, an SMS body). A `ResponsiveMenu`: a scrollable dropdown on a desktop, a bottom sheet of big
// rows on a phone (owner, 2026-10-09 — menus are hard to tap on a phone). The grouping lives here, once.

import React from "react";

import { ResponsiveMenu, type ActionSheetSection, type ResponsiveMenuProps } from "@em/ui/ActionSheet";

export interface VariableMenuItem {
  key: string;
  label: string;
  /** Group key — items sharing a group are shown together. */
  group?: string;
  /** Section heading shown once above the group. */
  groupLabel?: string;
}

export interface InsertVariableMenuProps {
  items: VariableMenuItem[];
  onInsert: (key: string) => void;
  /** The trigger element (a Button / ActionIcon / editor control). */
  children: ResponsiveMenuProps["target"];
  /** The phone sheet's title (default "Insert variable"). */
  title?: React.ReactNode;
  width?: number;
  /** Cap the dropdown height and scroll inside it, so a long list never runs off-screen. */
  maxHeight?: number;
  position?: ResponsiveMenuProps["position"];
}

/** The variables as menu sections: one per group, in first-seen order, headed by the group's label. */
export const variableSections = (items: VariableMenuItem[], onInsert: (key: string) => void): ActionSheetSection[] => {
  const order: string[] = [];
  const byGroup: Record<string, VariableMenuItem[]> = {};
  const labels: Record<string, string> = {};

  for (const item of items) {
    const g = item.group ?? "";

    if (!byGroup[g]) {
      byGroup[g] = [];
      order.push(g);
      labels[g] = item.groupLabel ?? "";
    }

    byGroup[g].push(item);
  }

  return order.map((g) => ({
    label: labels[g] || undefined,
    items: byGroup[g].map((v) => ({ label: v.label, onClick: () => onInsert(v.key) })),
  }));
};

/** A trigger + the grouped variables. Nothing is drawn when there are none. */
export const InsertVariableMenu: React.FC<InsertVariableMenuProps> = ({
  items,
  onInsert,
  children,
  title = "Insert variable",
  width = 260,
  maxHeight = 360,
  position = "bottom-end",
}) => {
  if (items.length === 0) {
    return null;
  }

  return (
    <ResponsiveMenu target={children} title={title} sections={variableSections(items, onInsert)} position={position} width={width} maxHeight={maxHeight} />
  );
};
